import {useEffect} from "react";
import {useParams} from "react-router-dom";

import {useImages} from "contexts/images";
import {useSetting} from "contexts/setting";
import {inlineSvgUrl} from "libs/bg-utils";
import livestreamAssets from "data/livestream-assets.json";
import {
  getLivestreamSeedBackgrounds,
  isLivestreamRasterProduct,
  normalizeLivestreamRasterBackgrounds,
} from "pages/Livestream/productBackgrounds";

// Seed each Livestream template's default background. Most products use the
// per-frame gradient SVG. Junie and .NET use one PNG with a per-frame crop.
// The SVG is stored on the backend; fetch and inline it so GradientControls can
// edit its stops. Use the bundled data URL if that fetch fails.
//
// Mirrors useEnsureDefaultEdits: seed only when nothing is set and not on a
// saved-scene route (the scene owns its background there).
export function useEnsureLivestreamBgs() {
  const {sceneId} = useParams();
  const {customBgs, customBgUploaded, loadCustomBgs} = useImages();
  const {product} = useSetting();

  useEffect(() => {
    if (sceneId) return;

    if (isLivestreamRasterProduct(product.value)) {
      const normalized = normalizeLivestreamRasterBackgrounds(
        product.value,
        customBgs,
        customBgUploaded
      );
      if (normalized !== customBgs) loadCustomBgs(normalized);
      return;
    }

    // A product switch clears the per-template records before its default
    // scene arrives. Seed that empty interval too; otherwise the gradient
    // editor has no SVG to inspect and disappears. A hydrated default always
    // wins because it replaces this temporary set of fallback SVGs.
    const hasBackground = Object.values(customBgs).some(
      (value) => typeof value === "string" && value
    );
    if (hasBackground) return;

    const seedBackgrounds = getLivestreamSeedBackgrounds(product.value);

    let cancelled = false;
    Promise.all(
      Object.entries(seedBackgrounds).map(async ([id, fallback]) => {
        const url = livestreamAssets[id]?.url;
        return [id, url ? (await inlineSvgUrl(url)) || fallback : fallback];
      })
    ).then((entries) => {
      // A product default can arrive while the fallback SVGs are downloading.
      // Its context update cancels this effect, so late fallback responses can
      // never overwrite the product-specific gradient stops.
      if (!cancelled) loadCustomBgs(Object.fromEntries(entries));
    });

    return () => {
      cancelled = true;
    };
  }, [sceneId, customBgs, customBgUploaded, loadCustomBgs, product.value]);
}
